/**
 * Floors API service for Whakahaere Safety Management System
 *
 * Migrated to typed `@tiaki/http-client` per ADR 113 §Decision.
 */

import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export interface Floor {
  id: number;
  name: string;
  building: string;
  school: string;
  status?: string;
  school_name?: string;
  building_name?: string;
  organization?: string;
  elevation?: number | null;
  ordering?: number;
  data?: Record<string, unknown> | null;
}

export interface FloorListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Floor[];
}

export interface FloorFilters {
  search?: string;
  is_active?: boolean;
  building?: number;
  page?: number;
  page_size?: number;
  ordering?: string;
}

/**
 * Get list of Floors with optional filters
 */
export async function getFloors(
  filters: FloorFilters = {},
): Promise<FloorListResponse> {
  const result = await client.GET('/api/v2/floor/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as FloorListResponse;
}

/**
 * Get a single Floor by ID
 */
export async function getFloorById(id: number): Promise<Floor> {
  const result = await client.GET('/api/v2/floor/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as Floor;
}

/**
 * Create a new Floor
 */
export async function createFloor(data: Omit<Floor, 'id'>): Promise<Floor> {
  const result = await client.POST('/api/v2/floor/', {
    body: data as never,
  });
  return unwrap(result) as unknown as Floor;
}

/**
 * Update an existing Floor
 */
export async function updateFloor(
  id: number,
  data: Partial<Omit<Floor, 'id'>>,
): Promise<Floor> {
  const result = await client.PATCH('/api/v2/floor/{id}/', {
    params: { path: { id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as Floor;
}

/**
 * Delete a Floor
 */
export async function deleteFloor(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/floor/{id}/', {
    params: { path: { id } as never },
  });
  unwrap(result);
}

/**
 * Reorder floors by updating their ordering values
 */
export async function reorderFloors(
  updates: { id: number; ordering: number }[],
): Promise<void> {
  await Promise.all(
    updates.map(async ({ id, ordering }) => {
      const result = await client.PATCH('/api/v2/floor/{id}/', {
        params: { path: { id } as never },
        body: { ordering } as never,
      });
      unwrap(result);
    }),
  );
}
